import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { MINUTE } from 'data/global';

export const FILE_CONTENT_QUERY_KEY = 'file-content';
export const CONTENT_CACHE_TIME = 30 * MINUTE; // 30 min

export const getFileContentQueryKey = (id: string, version?: number) => [
  FILE_CONTENT_QUERY_KEY,
  { id, version },
];

// bare axios (no app interceptors/auth) since `url` is a pre-signed storage url
export const fetchFileContent = async (url: string) => {
  const { data } = await axios.get<Blob>(url, { responseType: 'blob' });

  return data;
};

type UseFileContentQueryProps = {
  /** data room node id, chat session file id, ... — any stable file identifier */
  id: string;
  url?: string;
  version?: number;
  enabled?: boolean;
};

export default function useFileContentQuery({
  id,
  url,
  version,
  enabled = true,
}: UseFileContentQueryProps) {
  const getMixpanelProps = useGetMixpanelProps();

  return useQuery<Blob>({
    queryKey: getFileContentQueryKey(id, version),
    queryFn: () => fetchFileContent(url),
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: enabled && !!url && !!id,
    staleTime: CONTENT_CACHE_TIME,
    cacheTime: CONTENT_CACHE_TIME,
  });
}
